// core
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {Subscription} from 'rxjs';


// service
import {AdminUserOverviewService} from './service/admin-users.service';
import {AppConstants} from "../utils/constants";
import {OverviewUserForCustomerManager} from "../user/models/overview-user-customer-manager.model";
import {SearchAndPaginationService} from "../shared/services/search-and-pagination.service";
import {HttpParams} from "@angular/common/http";
import {PaginationComponent} from "../shared/pagination/pagination.component";
import {UtilsService} from '../utils/utils.service';
import {TranslateService} from '@ngx-translate/core';


@Component({
  selector: 'app-admin-users-overview',
  templateUrl: './admin-users-overview.component.html',
  styleUrls: ['./admin-users-overview.component.scss'],
  standalone: false
})

export class AdminUsersOverviewComponent implements OnInit, OnDestroy {

  @ViewChild(PaginationComponent, {static: true}) private paginationComponent: PaginationComponent;

  private listOfUsers: Array<OverviewUserForCustomerManager> = new Array<OverviewUserForCustomerManager>();

  private homePath = AppConstants.homePage;

  public loading = false;

  private totalNumberOfUser: number;

  private searchForm: UntypedFormGroup;

  // the list starts with the active users only, see the initial value of the status control
  private activeValueFilter = true;
  private activeStates: {value: string, label: string}[] = [];
  private langChangeSubscription: Subscription;

  // the list starts sorted by last name, and the sort is always sent, so that header and list cannot diverge
  public sortingObj = {
    sortLastNameField: {
      self: 'sortLastNameField',
      field: AppConstants.sortLastNameField,
      direction: 'ASC',
      sorting: true
    },
    sortFirstNameField: {
      self: 'sortFirstNameField',
      field: AppConstants.sortFirstNameField,
      direction: '',
      sorting: undefined
    },
    sortStatusField: {
      self: 'sortStatusField',
      field: AppConstants.sortStatusField,
      direction: '',
      sorting: undefined
    },
    sortCreationDateField: {
      self: 'sortCreationDateField',
      field: AppConstants.sortCreationDateField,
      direction: '',
      sorting: undefined
    },
    sortLastLoginField: {
      self: 'sortLastLoginField',
      field: AppConstants.sortLastLoginField,
      direction: '',
      sorting: undefined
    },
    sortConfirmedField: {
      self: 'sortConfirmedField',
      field: AppConstants.sortConfirmedField,
      direction: '',
      sorting: undefined
    },
  };

  private sortField = AppConstants.sortLastNameField;
  private sortDirection = 'ASC';

  constructor(
    private _router: Router,
    private _formBuilder: UntypedFormBuilder,
    private _adminUserOverviewService: AdminUserOverviewService,
    private _searchAndPaginationService: SearchAndPaginationService,
    private _utilsService: UtilsService,
    private _translateService: TranslateService,
  ) {
    this.searchForm = _formBuilder.group({
      searchTerm: [''],
      status: [AppConstants.active],
    });

    this.activeStates = this._utilsService.initializeSelectOrMultiselect([AppConstants.all, AppConstants.active, AppConstants.notActive]);
    this.langChangeSubscription = _translateService.onLangChange.subscribe(() => {
      this.activeStates = this._utilsService.initializeSelectOrMultiselect([AppConstants.all, AppConstants.active, AppConstants.notActive]);
    });
  }

  ngOnInit() {
    this.loading = true;
    // the search service is shared with the other lists, so a search term entered there must not carry over
    this._searchAndPaginationService.setSearchKeyWord('');
    this._searchAndPaginationService.setOffset(0);
    this.submitRequest();
  }

  ngOnDestroy() {
    this.langChangeSubscription?.unsubscribe();
  }

  public submitRequest() {
    this.loading = true;

    if (this._searchAndPaginationService.searchKeyWordNotEquals(this.searchForm.controls['searchTerm'].value)) {
      this._searchAndPaginationService.setSearchKeyWord(this.searchForm.controls['searchTerm'].value);
    }

    const httpParams = this._createSearchParams();
    this._adminUserOverviewService.loadUsers(httpParams)
      .subscribe((usersResponse) => {
        this.loading = false;
        this.listOfUsers = usersResponse.data;
        this.totalNumberOfUser = usersResponse.itemsTotal;
        this._searchAndPaginationService.setItemsPerPage(usersResponse.itemsPerPage);
        this.paginationComponent.setCurrentPageValue(Math.ceil(usersResponse.offset / usersResponse.itemsPerPage + 1));
      }, (error) => {
        this.loading = false;
      });
  }

  public getListOfUsers() {
    return this.listOfUsers;
  }

  public getHomePath() {
    return this.homePath;
  }

  public getSearchForm() {
    return this.searchForm;
  }

  public getActiveStates() {
    return this.activeStates;
  }

  public goToNewUserPage() {
    this._router.navigate([AppConstants.customerUserDetailPath], {queryParams: {type: 'create'}});
  }

  public goToEditPage(user) {
    this._router.navigate([AppConstants.customerUserDetailPath, user.id], {queryParams: {type: 'edit'}});
  }

  public getTotalNumberOfUsers() {
    return this.totalNumberOfUser;
  }

  public getItemsPerPage() {
    return this._searchAndPaginationService.getItemsPerPage();
  }

  public onPageSizeChange(pageSize: number) {
    this._searchAndPaginationService.setItemsPerPage(pageSize);
    this.submitRequest();
  }

  public onPageChange(page: number) {
    this._searchAndPaginationService.setOffset(this.getItemsPerPage() * (page - 1));
    this.submitRequest();
  }

  public onSelectStatus(option) {
    this.activeValueFilter = this._utilsService.getBoolVal(option.value);
    this._searchAndPaginationService.setOffset(0);
    this.submitRequest();
  }

  /**
   * @description Clears the search input field and all users will be fetched
   */
  public clearInputField() {
    this.searchForm.controls['searchTerm'].reset('');
    this._searchAndPaginationService.setSearchKeyWord('');
    this.submitRequest();
  }

  public sortTable(el) {
    for (const key in this.sortingObj) {
      if (el.self == key) {
        this.sortingObj[key].sorting = !this.sortingObj[key].sorting;
        if (this.sortingObj[key].sorting) {
          this.sortingObj[key].direction = 'ASC';
        } else {
          this.sortingObj[key].direction = 'DESC';
        }
        this.sortField = el.field;
        this.sortDirection = el.direction;
      } else {
        this.sortingObj[key].sorting = undefined;
      }
    }
    this._searchAndPaginationService.setOffset(0);
    this.submitRequest();
  }

  private _createSearchParams(): HttpParams {
    let httpParams = new HttpParams();
    if (this._searchAndPaginationService.getSearchKeyWord()) {
      httpParams = httpParams.set("searchTerm", this._searchAndPaginationService.getSearchKeyWord());
    }
    if (this.activeValueFilter != null) {
      httpParams = httpParams.set("active", this.activeValueFilter);
    }
    httpParams = httpParams.set("field", this.sortField);
    httpParams = httpParams.set("order", this.sortDirection);
    if (this._searchAndPaginationService.getOffset() != null) {
      httpParams = httpParams.set("offset", this._searchAndPaginationService.getOffset());
    }
    if (this._searchAndPaginationService.getItemsPerPage() != null) {
      httpParams = httpParams.set("pageSize", this._searchAndPaginationService.getItemsPerPage());
    }
    return httpParams;
  }

}
